/**
 * spriteWarm — the sprite preload that runs behind the boot splash
 * (owner ask, 2026-10-07: "use the loading screen to preload everything so
 * the flicker does not happen mid-use").
 *
 * WHAT FLICKERED. Every base and shiny species, and the alt forms, ship in
 * the binary as small WebPs (client/public/sprites-bundled), served through
 * Capacitor's asset bridge. A grid that mounts hundreds of cells at once
 * fires hundreds of bridge requests at once; some stall, the 3 s guard in
 * CaughtSprite walks those cells on to the CDN, and the cell blinks blank
 * and repaints. Scrolling into culled rows paid the same first-load pop.
 *
 * WHAT THIS DOES. It loads the same URLs ONCE, a few at a time, starting
 * under the splash with the first screen's cells and carrying on in the
 * background, and keeps each Image object so WebKit holds the bytes in its
 * memory cache. A cell whose URL is warm paints from that cache on its first
 * frame (CaughtSprite switches it to eager loading), so nothing fetches, nothing
 * stalls and nothing pops in mid-use.
 *
 * Native only for the full set: on the web the same art is CDN traffic and a
 * visitor should not download 2,500 images to look at one page; there, only
 * the first screen is warmed.
 */
import { isNative } from "@/lib/platform";
import { bundledArtworkUrl } from "@/lib/pokemonArtwork";
import { FORM_GROUPS_BY_SPECIES_ID } from "@/lib/generated/pokemonForms.generated";
import { parseFormSpriteId } from "@/lib/formSpriteCandidates";

/** National dex size the bundle covers 1..N for. */
const SPECIES_TOTAL = 1025;
/** How many cells the Dex's first screen can show (15 rows of 4). */
export const FIRST_SCREEN_CELLS = 60;
/** Parallel loads: enough to keep the bridge busy, few enough never to stall it. */
export const WARM_CONCURRENCY = 6;

const warm = new Map<string, HTMLImageElement>();
let started: { firstScreen: Promise<void>; all: Promise<void> } | null = null;

/** True when this URL has finished loading through the warm cache. */
export function isSpriteWarm(url: string | null | undefined): boolean {
  if (!url) return false;
  const img = warm.get(url);
  return !!img && img.complete && img.naturalWidth > 0;
}

/** The form ids above 1025 the bundle carries (the same rule the bundler uses). */
export function bundledFormIds(): number[] {
  const ids = new Set<number>();
  for (const group of FORM_GROUPS_BY_SPECIES_ID.values()) {
    for (const f of group.forms) {
      const id = parseFormSpriteId(f.spritePath);
      if (id != null && id > SPECIES_TOTAL) ids.add(id);
    }
  }
  return [...ids].sort((a, b) => a - b);
}

/**
 * Every bundled URL in warm order: the first screen's base art, the rest of
 * the base art, then shiny, then the forms. `firstScreen` is the prefix the
 * splash waits for.
 */
export function spriteWarmPlan(): { firstScreen: string[]; rest: string[] } {
  const firstScreen: string[] = [];
  const rest: string[] = [];
  for (let id = 1; id <= SPECIES_TOTAL; id++) {
    (id <= FIRST_SCREEN_CELLS ? firstScreen : rest).push(bundledArtworkUrl(id));
  }
  for (let id = 1; id <= SPECIES_TOTAL; id++) rest.push(bundledArtworkUrl(id, { shiny: true }));
  for (const id of bundledFormIds()) {
    rest.push(bundledArtworkUrl(id), bundledArtworkUrl(id, { shiny: true }));
  }
  return { firstScreen, rest };
}

function loadOne(url: string): Promise<void> {
  return new Promise((resolve) => {
    if (warm.has(url)) return resolve();
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve();
    // A 404 (a tile the bundle lacks) is simply not warm; CaughtSprite's own
    // chain handles it as before.
    img.onerror = () => {
      warm.delete(url);
      resolve();
    };
    warm.set(url, img);
    img.src = url;
  });
}

async function nextFrame(): Promise<void> {
  await new Promise<void>((r) => setTimeout(r, 0));
}

/**
 * Load `urls` a few at a time. Pauses while the page is hidden (a backgrounded
 * app should not be pulling art), and yields between batches so the main
 * thread stays free for the trainer.
 */
export async function warmSpriteUrls(urls: readonly string[], concurrency = WARM_CONCURRENCY): Promise<void> {
  let i = 0;
  const worker = async () => {
    while (i < urls.length) {
      if (typeof document !== "undefined" && document.hidden) {
        await new Promise<void>((r) => setTimeout(r, 500));
        continue;
      }
      const url = urls[i++];
      await loadOne(url);
      await nextFrame();
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
}

/**
 * Start the boot warm once. Returns the two milestones: `firstScreen` for the
 * splash to wait on (capped by the caller), `all` for anyone curious.
 */
export function startSpriteWarm(): { firstScreen: Promise<void>; all: Promise<void> } {
  if (started) return started;
  if (typeof Image === "undefined") {
    started = { firstScreen: Promise.resolve(), all: Promise.resolve() };
    return started;
  }
  const plan = spriteWarmPlan();
  const firstScreen = warmSpriteUrls(plan.firstScreen).catch(() => undefined);
  const all = firstScreen.then(() => (isNative() ? warmSpriteUrls(plan.rest) : undefined)).catch(() => undefined);
  started = { firstScreen, all };
  return started;
}

/** Tests only. */
export function __resetSpriteWarmForTests(): void {
  warm.clear();
  started = null;
}
